import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import StatusBadge from '../../components/StatusBadge';
import API from '../../utils/api';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Edit3, Clock, Send, CheckCircle, Inbox, PlusCircle, X } from 'lucide-react';
import { format } from 'date-fns';
import ArticleStats from '../../components/ArticleStats';
import './EditorDashboard.css';

export default function EditorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignedArticles, setAssignedArticles] = useState([]);
  const [availableArticles, setAvailableArticles] = useState([]);
  const [activeTab, setActiveTab] = useState('assigned');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ articles: [], totalViews: 0, avgViews: 0 });
  const [categories, setCategories] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ categories: [], title: '', who: '', what: '', when: '', where: '', why: '', how: '', additionalContext: '', photos: [] });
  const [creating, setCreating] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchData();
    API.get('/categories').then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignedRes, availableRes] = await Promise.all([
        API.get('/articles/editor/assigned'),
        API.get('/articles/editor/available'),
      ]);
      setAssignedArticles(assignedRes.data.articles || []);
      setAvailableArticles(availableRes.data.articles || []);
    } catch { toast.error('Gagal memuat data'); }
    finally { setLoading(false); }
    API.get('/articles/editor/stats').then(r => setStats({ articles: r.data.articles || [], totalViews: r.data.totalViews || 0, avgViews: r.data.avgViews || 0 })).catch(() => {});
  };

  const handleAssign = async (articleId) => {
    try {
      await API.patch(`/articles/editor/${articleId}/assign`);
      toast.success('Artikel berhasil diambil');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal mengambil artikel'); }
  };

  const handleCreate = async () => {
    if (!createForm.categories.length) { toast.error('Pilih minimal satu kategori'); return; }
    const required = ['who','what','when','where','why','how'];
    for (const f of required) {
      if (!createForm[f]?.trim()) { toast.error(`Field "${f.toUpperCase()}" wajib diisi`); return; }
    }
    try {
      setCreating(true);
      const res = await API.post('/articles/editor/create', createForm);
      toast.success('Artikel baru dibuat');
      setShowCreateModal(false);
      setCreateForm({ categories: [], title: '', who: '', what: '', when: '', where: '', why: '', how: '', additionalContext: '', photos: [] });
      navigate(`/editor/artikel/${res.data.article._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal membuat artikel');
    } finally {
      setCreating(false);
    }
  };

  const handleCreatePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (createForm.photos.length >= 5) { toast.error('Maksimal 5 foto'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Maksimal 15MB per foto'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await API.post('/upload/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCreateForm(f => ({ ...f, photos: [...f.photos, { url: res.data.url, caption: '' }] }));
      toast.success('Foto ditambahkan');
    } catch { toast.error('Gagal upload foto'); }
    finally { setUploadingPhoto(false); e.target.value = ''; }
  };

  const assigned = assignedArticles;
  const inProgress = assigned.filter(a => ['writing','revision_editor','revision_reader'].includes(a.status)).length;
  const pendingProducer = assigned.filter(a => a.status === 'review_producer').length;
  const published = assigned.filter(a => a.status === 'published').length;

  const displayArticles = activeTab === 'assigned' ? assignedArticles : availableArticles;

  return (
    <DashboardLayout title={`Selamat datang, ${user?.name?.split(' ')[0] || ''}`} subtitle="Kelola dan kerjakan artikel yang ditugaskan kepada Anda">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreateModal(true)}>
          <PlusCircle size={14} /> Buat Artikel Baru
        </button>
      </div>

      <div className="stats-row">
        {[
          { label: 'Sedang Dikerjakan', val: inProgress, icon: <Edit3 size={20} />, color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
          { label: 'Menunggu Producer', val: pendingProducer, icon: <Send size={20} />, color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
          { label: 'Tersedia Diambil', val: availableArticles.length, icon: <Inbox size={20} />, color: 'var(--ki-text)', bg: 'rgba(212,105,10,0.10)' },
          { label: 'Terpublikasi', val: published, icon: <CheckCircle size={20} />, color: 'var(--ki-text)', bg: 'rgba(212,105,10,0.10)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-card-glow" style={{ background: s.bg }} />
            <div style={{ color: s.color }}>{s.icon}</div>
            <div className="stat-val">{s.val}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="ki-tabs">
        <button className={`ki-tab ${activeTab === 'assigned' ? 'active' : ''}`} onClick={() => setActiveTab('assigned')}>
          Ditugaskan ke Saya
          <span style={{ marginLeft: 7, padding: '1px 8px', fontSize: 11, fontWeight: 700, borderRadius: 20, background: 'var(--in-blue)', color: '#fff' }}>{assignedArticles.length}</span>
        </button>
        <button className={`ki-tab ${activeTab === 'available' ? 'active' : ''}`} onClick={() => setActiveTab('available')}>
          Tersedia
          <span style={{ marginLeft: 7, padding: '1px 8px', fontSize: 11, fontWeight: 700, borderRadius: 20, background: availableArticles.length > 0 ? 'var(--in-orange)' : 'var(--in-bg-4)', color: availableArticles.length > 0 ? '#fff' : 'var(--in-text-faint)' }}>{availableArticles.length}</span>
        </button>
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(4)].map((_, i) => <div key={i} className="shimmer" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : displayArticles.length === 0 ? (
        <div className="ki-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <Inbox size={40} style={{ color: 'var(--ki-text-faint)', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
            {activeTab === 'assigned' ? 'Belum ada artikel ditugaskan' : 'Tidak ada artikel tersedia'}
          </div>
          <p style={{ color: 'var(--ki-text-muted)', fontSize: 14 }}>
            {activeTab === 'assigned' ? 'Ambil artikel dari tab "Tersedia"' : 'Semua artikel sudah diambil editor lain'}
          </p>
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr><th>Topik Artikel</th><th>Kategori</th><th>Pembuat</th><th>Status</th><th>Waktu</th><th>Aksi</th></tr>
            </thead>
            <tbody>
              {displayArticles.map(a => (
                <tr key={a._id} style={{ background: ['revision_editor','revision_reader'].includes(a.status) ? 'rgba(245,158,11,0.04)' : '' }}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 14, maxWidth: 280 }}>{a.title || 'Judul belum ada'}</div>
                    {a.status === 'revision_editor' && <div style={{ fontSize: 11, color: 'var(--ki-text)', marginTop: 2 }}>Revisi dari Producer</div>}
                    {a.status === 'revision_reader' && <div style={{ fontSize: 11, color: 'var(--ki-text)', marginTop: 2 }}>Revisi dari Pengaju</div>}
                  </td>
                  <td><span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(0,21,64,0.10)', color: 'var(--in-blue)', fontWeight: 600 }}>{a.category?.name || '-'}</span></td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>{a.requestedBy?.name || '-'}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--ki-text-faint)' }}>{formatDistanceToNow(new Date(a.updatedAt), { addSuffix: true, locale: id })}</td>
                  <td>
                    {activeTab === 'available'
                      ? <button className="btn btn-primary btn-sm" onClick={() => handleAssign(a._id)}>Ambil</button>
                      : ['writing'].includes(a.status)
                        ? <Link to={`/editor/artikel/${a._id}`} className="btn btn-secondary btn-sm">Kerjakan</Link>
                        : ['revision_editor','revision_reader'].includes(a.status)
                          ? <Link to={`/editor/artikel/${a._id}`} className="btn btn-secondary btn-sm">Revisi</Link>
                          : <Link to={`/editor/artikel/${a._id}`} className="btn btn-secondary btn-sm" style={{ opacity: 0.7 }}>Lihat</Link>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {showCreateModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className="modal-box" style={{ maxWidth: 600 }}>
            <div className="modal-head">
              <div style={{ fontSize: 16, fontWeight: 700 }}>Buat Artikel Baru</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setShowCreateModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: 'var(--ki-text-muted)', marginBottom: 20 }}>
                Isi detail artikel berikut. Judul dan konten artikel dapat dilengkapi di editor setelah ini.
              </p>
              <div className="form-group">
                <label className="form-label">Kategori * <span style={{ fontWeight: 400, color: 'var(--in-text-faint)' }}>(boleh lebih dari satu)</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {categories.map(c => {
                    const active = createForm.categories.includes(c._id);
                    return (
                      <button
                        key={c._id} type="button"
                        onClick={() => setCreateForm(f => ({ ...f, categories: active ? f.categories.filter(x => x !== c._id) : [...f.categories, c._id] }))}
                        style={{
                          padding: '6px 14px', borderRadius: 100, fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                          background: active ? (c.color || 'var(--in-blue)') : 'var(--in-bg-3)',
                          color: active ? '#fff' : 'var(--in-text-muted)',
                          border: '1px solid ' + (active ? (c.color || 'var(--in-blue)') : 'var(--in-border)'),
                        }}
                      >{c.name}</button>
                    );
                  })}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Judul Awal (opsional)</label>
                <input className="form-input" placeholder="Bisa dilengkapi di editor..." value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ki-text-muted)', margin: '20px 0 12px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Detail 5W1H *</div>
              {[
                { key: 'who',   label: 'Siapa (Who)',      placeholder: 'Siapa yang terlibat?' },
                { key: 'what',  label: 'Apa (What)',       placeholder: 'Apa yang terjadi atau dibahas?' },
                { key: 'when',  label: 'Kapan (When)',     placeholder: 'Kapan peristiwa terjadi?' },
                { key: 'where', label: 'Di mana (Where)',  placeholder: 'Di mana lokasi atau konteksnya?' },
                { key: 'why',   label: 'Mengapa (Why)',    placeholder: 'Mengapa hal ini penting?' },
                { key: 'how',   label: 'Bagaimana (How)',  placeholder: 'Bagaimana prosesnya?' },
              ].map(f => (
                <div className="form-group" key={f.key}>
                  <label className="form-label">{f.label} *</label>
                  <textarea className="form-input" rows={2} placeholder={f.placeholder} value={createForm[f.key]} onChange={e => setCreateForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ resize: 'vertical', minHeight: 48 }} />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Konteks Tambahan (opsional)</label>
                <textarea className="form-input" rows={2} placeholder="Informasi tambahan yang relevan..." value={createForm.additionalContext} onChange={e => setCreateForm(f => ({ ...f, additionalContext: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Foto (maks 5, 15MB/foto)</label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600, background: 'var(--in-bg-3)', border: '1px solid var(--in-border)', color: 'var(--in-text)', opacity: uploadingPhoto ? 0.6 : 1, pointerEvents: uploadingPhoto || createForm.photos.length >= 5 ? 'none' : 'auto' }}>
                  {uploadingPhoto ? 'Mengupload...' : '+ Tambah Foto'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCreatePhotoUpload} />
                </label>
                {createForm.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {createForm.photos.map((ph, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={ph.url} alt="" style={{ width: 72, height: 52, objectFit: 'cover', borderRadius: 6 }} />
                        <button onClick={() => setCreateForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }))}
                          style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#DC2626', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}>Batal</button>
                <button className="btn btn-primary" disabled={creating || !createForm.categories.length} onClick={handleCreate}>
                  {creating ? 'Membuat...' : 'Buat & Mulai Menulis'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ArticleStats
        articles={stats.articles}
        totalViews={stats.totalViews}
        avgViews={stats.avgViews}
        label="Statistik Artikel yang Saya Edit"
        secondaryCol="requestedBy"
        secondaryLabel="Pengaju"
      />
    </DashboardLayout>
  );
}
